// deployment_class: customer_env_allowed
/**
 * usePreferences — User Preferences spec §7.1.
 *
 * TanStack Query hook over GET /v3/account/preferences. Returns merged
 * preferences with DEFAULT_PREFERENCES applied client-side as the fallback
 * while fetching, so consumers never receive `undefined` and can safely
 * destructure prefs.visual.density on first render.
 *
 * Per Plan 13 dispatch instructions: this hook is wired up in this dispatch
 * (the User Preferences backend landed in Part 9 but the frontend hook had
 * not been created yet).
 *
 * Application points (per spec §7.2):
 *   - visual.density        → CSS variable on layout container
 *   - visual.font_scale     → CSS variable
 *   - visual.reduced_motion → data attribute (auto-honors OS pref)
 *   - routing.default_landing_per_portal → portal initial route
 *   - locale.timezone        → DateTime components
 *   - locale.date_format     → DateTime components
 */

import { useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@v3-shell/api/api-client-base';

// ─────────────────────────────────────────────────────────────
// Types — mirror src/v3/auth/preferences-types.ts exactly. Kept inline
// (not imported) because the SPA bundle does NOT compile against the
// backend tsconfig; backend types use .ts ESM import suffix that the
// frontend tsconfig does not allow.
// ─────────────────────────────────────────────────────────────

export type DensityPreference = 'comfortable' | 'compact' | 'ultra_compact';
export type FontScalePreference = '1.0' | '1.15' | '1.3';
export type ReducedMotionPreference = boolean | 'auto';
export type ThemePreference = 'light' | 'dark' | 'system';
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
export type CurrencyFormat = 'comma' | 'space' | 'period';
export type AIPanelDefaultState = 'open' | 'collapsed';
export type AIResponseStyle = 'concise' | 'detailed';
export type AITranscriptRetentionDays = 30 | 90 | 365 | 'until_deleted';
export type EmailFrequency =
  | 'immediate'
  | 'daily_digest'
  | 'weekly_digest'
  | 'off';
export type NotificationCategory =
  | 'security'
  | 'deal_updates'
  | 'signoff_requests'
  | 'system_alerts'
  | 'marketing';
export type PortalContext =
  | 'data_analytics'
  | 'sales'
  | 'marketplace'
  | 'partnership';

export interface VisualPreferences {
  theme: ThemePreference;
  density: DensityPreference;
  font_scale: FontScalePreference;
  reduced_motion: ReducedMotionPreference;
  // Accessibility overrides (Settings & Account IA spec §6 · on the WCAG 2.2 AA floor).
  high_contrast: boolean;
  colorblind_safe: boolean;
  dyslexia_font: boolean;
  large_targets: boolean;
  /** Dashboard backdrop image opacity 0–100 (%); 0 hides it, 100 shows it full. */
  dashboard_backdrop_opacity: number;
  /** Which backdrop image dashboards + account pages use. */
  dashboard_backdrop: DashboardBackdrop;
  /** Module surface opacity 0–100 (%); 0 = translucent glass, 100 = solid. */
  module_opacity: number;
  /** Module edge definition 0–100 (%); scales the border + drop shadow. */
  module_definition: number;
  /** Detail slide-over ("glass door") panel opacity 0–100 (%); its own knob. */
  drill_panel_opacity: number;
  /** Fine text-size % (80–150), composes on top of the font_scale preset. */
  text_scale: number;
}

/** The selectable backdrop images (public/dashboard-backdrops/<id>.jpg). */
export type DashboardBackdrop =
  | 'meadow'
  | 'hills'
  | 'sunrise'
  | 'bigsky'
  | 'dusk'
  | 'exec'
  | 'dawn';

export interface RoutingPreferences {
  default_landing_per_portal: Partial<Record<PortalContext, string>>;
  column_visibility: Record<string, { hidden_columns: string[]; column_order: string[] }>;
  saved_filters: Record<string, unknown[]>;
}

export interface NotificationPreferences {
  email_frequency: EmailFrequency;
  in_app_enabled: boolean;
  categories: Record<NotificationCategory, boolean>;
}

export interface AIPreferences {
  panel_default_state: AIPanelDefaultState;
  response_style: AIResponseStyle;
  voice_output_enabled: boolean;
  transcript_retention_days: AITranscriptRetentionDays;
}

export interface LocalePreferences {
  timezone: string;
  date_format: DateFormat;
  currency_format: CurrencyFormat;
}

export interface PreferencesV1 {
  visual: VisualPreferences;
  routing: RoutingPreferences;
  notifications: NotificationPreferences;
  ai: AIPreferences;
  locale: LocalePreferences;
}

export interface PreferencesResponse {
  user_id: string;
  preferences: PreferencesV1;
  schema_version: string;
  updated_at: string;
}

// ─────────────────────────────────────────────────────────────
// Defaults — mirror DEFAULT_PREFERENCES from the backend
// ─────────────────────────────────────────────────────────────

export const DEFAULT_PREFERENCES: PreferencesV1 = {
  visual: {
    // Default UI for ALL accounts (E3 2026-07-14). Mirrors DEFAULT_VISUAL in
    // src/v3/auth/preferences-defaults.ts — keep in lockstep.
    theme: 'light',
    density: 'compact',
    font_scale: '1.0',
    reduced_motion: 'auto',
    high_contrast: false,
    colorblind_safe: false,
    dyslexia_font: false,
    large_targets: false,
    dashboard_backdrop_opacity: 0,
    dashboard_backdrop: 'dusk',
    module_opacity: 100,
    module_definition: 100,
    drill_panel_opacity: 82,
    text_scale: 100,
  },
  routing: {
    default_landing_per_portal: {},
    column_visibility: {},
    saved_filters: {},
  },
  notifications: {
    email_frequency: 'immediate',
    in_app_enabled: true,
    categories: {
      security: true,
      deal_updates: true,
      signoff_requests: true,
      system_alerts: true,
      marketing: false,
    },
  },
  ai: {
    panel_default_state: 'collapsed',
    response_style: 'concise',
    voice_output_enabled: false,
    transcript_retention_days: 30,
  },
  locale: {
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'America/Chicago',
    date_format: 'MM/DD/YYYY',
    currency_format: 'comma',
  },
};

const PREFS_QUERY_KEY = ['user-preferences'] as const;

/**
 * Read the current user's merged preferences. Returns DEFAULT_PREFERENCES
 * while the query is loading or if the user is unauthenticated (the API
 * 401's are caught and we silently fall back).
 */
export function usePreferences(): PreferencesV1 {
  const query = useQuery({
    queryKey: PREFS_QUERY_KEY,
    queryFn: async (): Promise<PreferencesV1> => {
      try {
        const res = await apiFetch<PreferencesResponse>('/v3/account/preferences');
        return res.preferences;
      } catch {
        // Anonymous or 401 — fall back to defaults so consumers keep working.
        return DEFAULT_PREFERENCES;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  return query.data ?? DEFAULT_PREFERENCES;
}

export interface UseUpdatePreferenceReturn {
  isPending: boolean;
  update: (patch: Partial<PreferencesV1>) => void;
  reset: (categories?: (keyof PreferencesV1)[]) => Promise<PreferencesV1>;
  error: Error | null;
}

/**
 * Mutation surface for PATCH /v3/account/preferences and POST /reset.
 *
 * `update` is OPTIMISTIC + DEBOUNCED. Continuous controls (the backdrop /
 * module sliders) fire `update` on every tick; a naive implementation that
 * PATCHed + invalidated (→ refetch) per tick made the sliders unusable — the
 * thumb is bound to the query cache, so it could not advance until each network
 * round-trip landed. Here we patch the cache SYNCHRONOUSLY (instant thumb +
 * instant CSS vars via useApplyPreferences) and persist ONCE, 250 ms after the
 * user stops moving, coalescing all intermediate values into a single PATCH.
 */
export function useUpdatePreference(): UseUpdatePreferenceReturn {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (patch: Partial<PreferencesV1>): Promise<PreferencesV1> => {
      const res = await apiFetch<PreferencesResponse>(
        '/v3/account/preferences',
        {
          method: 'PATCH',
          body: JSON.stringify(patch),
        },
      );
      return res.preferences;
    },
    // Server is the source of truth on ack — write it straight into the cache.
    // Deliberately NO invalidate: a refetch mid-drag would race the optimistic
    // value and make the slider jump. The cache already holds the latest.
    onSuccess: (data) => {
      qc.setQueryData(PREFS_QUERY_KEY, data);
    },
  });

  const pending = useRef<Partial<PreferencesV1>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback((): void => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length > 0) {
      mutation.mutate(patch);
    }
  }, [mutation]);

  const update = useCallback(
    (patch: Partial<PreferencesV1>): void => {
      // 1. Optimistic — patch the cache NOW so the thumb + CSS vars move this
      //    frame. Categories arrive whole (setVisual sends the full visual
      //    object), so a top-level spread is the correct merge.
      qc.setQueryData<PreferencesV1>(PREFS_QUERY_KEY, (old) => ({
        ...(old ?? DEFAULT_PREFERENCES),
        ...patch,
      }));
      // 2. Coalesce + debounce the network persist.
      pending.current = { ...pending.current, ...patch };
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flush, 250);
    },
    [qc, flush],
  );

  // Persist any still-pending patch if the screen unmounts mid-debounce, so a
  // final drag value is never lost.
  useEffect(() => {
    return () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
        const patch = pending.current;
        pending.current = {};
        if (Object.keys(patch).length > 0) mutation.mutate(patch);
      }
    };
    // Empty deps on purpose: `mutation` is stable for the hook's lifetime, and this
    // effect exists ONLY to flush pending edits on unmount — re-running it on any
    // dependency change would flush mid-session.
    //
    // This previously carried an `eslint-disable-next-line react-hooks/exhaustive-deps`
    // directive for a rule that is not installed, which ESLint 9 reports as an error
    // ("Definition for rule was not found"). Adopting eslint-plugin-react-hooks is
    // worth doing — exhaustive-deps catches real bugs — but it is a third-party
    // dependency and needs its N50 Mariana-Trench audit first, so it is tracked rather
    // than slipped in here.
  }, []);

  const reset = async (
    categories?: (keyof PreferencesV1)[],
  ): Promise<PreferencesV1> => {
    // A reset supersedes any queued optimistic persist.
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    pending.current = {};
    const body =
      categories && categories.length > 0
        ? { categories }
        : { all: true };
    const res = await apiFetch<PreferencesResponse>(
      '/v3/account/preferences/reset',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
    qc.setQueryData(PREFS_QUERY_KEY, res.preferences);
    return res.preferences;
  };

  return {
    isPending: mutation.isPending,
    update,
    reset,
    error: mutation.error,
  };
}

/**
 * Resolve the effective reduced_motion boolean by combining the user
 * preference with the OS-level prefers-reduced-motion media query.
 *
 * - prefs.reduced_motion === true  → reduced motion ALWAYS
 * - prefs.reduced_motion === false → reduced motion NEVER (overrides OS)
 * - prefs.reduced_motion === 'auto'→ honor OS preference
 */
export function effectiveReducedMotion(
  prefs: PreferencesV1,
): boolean {
  const userPref = prefs.visual.reduced_motion;
  if (userPref === true) return true;
  if (userPref === false) return false;
  // 'auto' — defer to OS.
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Resolve `visual.theme` to a concrete `'light' | 'dark'`:
 * - `'light'` / `'dark'`  → that value (user-pinned, overrides OS)
 * - `'system'`            → honor `prefers-color-scheme` (defaults light if
 *   unavailable / SSR). Mirrors the `effectiveReducedMotion` pattern.
 */
export function effectiveTheme(prefs: PreferencesV1): 'light' | 'dark' {
  const t = prefs.visual.theme;
  if (t === 'light' || t === 'dark') return t;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
